"use client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Plus, Package, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/src/store/hooks";
import Image from "next/image";
import { images } from "@/public";
import {
  API_ENDPOINTS,
  GenericObjectInterface,
  makeApiRequest,
  headersList,
} from "@/src/utilities";
import {
  updateProductsSelectedForComparison,
  updateSelectedSubcategories,
  updateComparisonLockedSubcategory,
  updateSelectedCategories,
} from "@/src/store/reducers/ungatedContent/ungatedContentSlice";
import { useRouter, useSearchParams } from "next/navigation";
import ComparisonTable from "../../custom/Comparison/ComparisonTable";
import { RootState } from "@/src/store";
import { toast } from "react-toastify";
import { renderStars } from "../../custom/RenderStar";
import { useQuery } from "@tanstack/react-query";
import { ITEMS_PER_PAGE } from "@/src/utilities/constants/constants";
import { PaginationButtons } from "../../custom/PaginationButtons";
import SectionHeader from "../../custom/SeactionHeader";
import CompareToast from "./CompareToast";
import { CustomDialog } from "../../custom/CustomDialog";
import InquiryForm from "../../custom/InquiryForm";
import SkeletonLoader from "../../custom/Skeleton";
import CategoryFilter from "../../custom/CategoryFilter";
import { transformCategories } from "@/src/utilities/helper/helper";

/**
 * @description This component is used to display the software reviews section or products section
 * The pros and cons are commented out as per client requirement
 * @returns {React.FC}
 */
const SoftwareReviewsSection = () => {
  const searchParams = useSearchParams();
  const categoryThroughUrl = searchParams.get("category");
  const subCategoryThroughUrl = searchParams.get("subCategory");
  const reviewRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );

  const router = useRouter();
  const { productCategories: categories, comparedProducts, categoriesInitialized } = useAppSelector(
    (root) => root.unGatedContentStateReducer
  );
  const {
    selectedCategories,
    selectedSubcategories,
    comparisonLockedSubcategory,
  } = useAppSelector(
    (state: RootState) => state.unGatedContentStateReducer.comparedProducts
  );

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [page, setPage] = useState(1);

  useEffect(() => {
    if (subCategoryThroughUrl) {
      dispatch(updateSelectedSubcategories(subCategoryThroughUrl));
    }
  }, [subCategoryThroughUrl]);

  useEffect(() => {
    if (categoryThroughUrl) {
      dispatch(updateSelectedCategories(categoryThroughUrl));
    }
  }, [categoryThroughUrl]);

  /**
   * useQuery for fetch products
   */
  const { data: productDetails, isLoading }: any = useQuery({
    queryKey: ["products", selectedSubcategories, page],
    queryFn: () => {
      return makeApiRequest(
        "GET",
        `${API_ENDPOINTS.GET_PRODUCTS_BY_CATEGORY}/${selectedSubcategories}?pagination[page]=${page}&pagination[pageSize]=${ITEMS_PER_PAGE}`,
        null,
        headersList
      ).then((res) => res.data);
    },
    enabled: !!selectedSubcategories && categoriesInitialized, // Only run query when categories are initialized and subcategory is set
  });

  const totalPages = useMemo(() => {
    return productDetails?.meta?.pagination?.pageCount || 1;
  }, [productDetails]);

  /**
   * @description This function is used to handle the compare action
   * @param {string} id - The product documentId
   * @suggestion in future when the size of products is increased in comparison, then productDetails would have limited data and wont be able to warn about the subcategory mismatch
   * so this warning must be handled from backend
   */
  const handleCompare = useCallback(
    (product: GenericObjectInterface) => {
      if (!product || !product.documentId) {
        return;
      }

      // Check if the product is already selected
      const isCurrentlySelected = comparedProducts?.selectedForComparison?.find(
        (item) => item.documentId === product.documentId
      );

      // Get all subcategory IDs from the current product
      const productSubcategoryIds: string[] = product?.category_references?.map(
        (item: GenericObjectInterface) => item.documentId
      ) || [];

      console.log("current product : ", product);
      console.log("current product subcategories: ", productSubcategoryIds);
      console.log(
        "comparison locked subcategory: ",
        comparisonLockedSubcategory
      );

      // If removing (already selected)
      if (isCurrentlySelected) {
        dispatch(updateProductsSelectedForComparison(product));
        setToastMessage(
          `${product?.name || "Product"
          } has been removed from your comparison list.`
        );

        // If this was the last product, clear the locked subcategory
        if (
          comparedProducts?.selectedForComparison &&
          comparedProducts?.selectedForComparison?.length === 1
        ) {
          dispatch(updateComparisonLockedSubcategory(null));
        }
        return;
      }

      // If adding - Check if limit is reached
      if (
        comparedProducts?.selectedForComparison &&
        comparedProducts?.selectedForComparison?.length >= 4
      ) {
        toast.warning("Maximum 4 products can be compared", {
          position: "bottom-right",
        });
        return;
      }

      console.log(
        "selected Product length: ",
        comparedProducts?.selectedForComparison.length
      );

      // If nothing selected yet, lock to the currently selected subcategory (if product has it)
      // Otherwise, lock to the first subcategory the product has
      if (
        comparedProducts?.selectedForComparison &&
        comparedProducts?.selectedForComparison?.length === 0
      ) {
        //@ts-ignore
        const lockToSubcategory = productSubcategoryIds.includes(selectedSubcategories)
          ? selectedSubcategories
          : productSubcategoryIds[0];

        console.log(
          "Locking comparison to subcategory: ",
          lockToSubcategory
        );

        dispatch(updateComparisonLockedSubcategory(lockToSubcategory));
        dispatch(updateProductsSelectedForComparison(product));
        setToastMessage(
          `${product?.name || "Product"
          } has been added to your comparison list!`
        );
        return;
      }

      // Check if the current product has the locked subcategory (shares at least one common subcategory)
      const hasLockedSubcategory = comparisonLockedSubcategory ? productSubcategoryIds.includes(comparisonLockedSubcategory) : false;

      if (hasLockedSubcategory) {
        dispatch(updateProductsSelectedForComparison(product));
        setToastMessage(
          `${product?.name || "Product"
          } has been added to your comparison list!`
        );
      } else {
        toast.warning(
          "You can only compare products from the same subcategory",
          {
            position: "bottom-right",
          }
        );
      }
    },
    [
      comparedProducts?.selectedForComparison,
      comparisonLockedSubcategory,
      selectedSubcategories,
      dispatch,
    ]
  );

  const handleInquiryModalOpen = (documentId: string) => {
    setSelectedProductId(documentId);
    setIsInquiryModalOpen(true);
  };

  const handleInquiryModalClose = () => {
    setIsInquiryModalOpen(false);
    setSelectedProductId(null);
  };

  /**
   * @description This useEffect is used to set the selected subcategory documentId and updated in redux
   * @param {string} selectedCategories - The selected category documentId
   */
  useEffect(() => {
    const selectedCategory = categories?.find(
      (category: GenericObjectInterface) =>
        category?.documentId === selectedCategories
    );
    if (selectedCategory) {
      // Check if current subcategory is valid for this category
      const isValidSubcategory = selectedCategory?.subcategories?.some(
        (sub: GenericObjectInterface) =>
          sub?.documentId === selectedSubcategories
      );

      // Only update if subcategory is null or invalid for current category
      // if (!selectedSubcategories || !isValidSubcategory) {
      //   const selectedCurrentSubCategory =
      //     selectedCategory?.subcategories?.[0]?.documentId;
      //   selectedCurrentSubCategory
      //     ? dispatch(updateSelectedSubcategories(selectedCurrentSubCategory))
      //     : null;
      // }
    }
    // dispatch(updateSelectedSubcategories(selectedSubcategories));
  }, [selectedCategories]);

  useEffect(() => {
    console.log("transformed categories", transformCategories(categories)?.[0]?.subcategories, categories?.[2]?.subcategories);
  }, []);

  // Reset page to 1 when subcategory changes
  useEffect(() => {
    setPage(1);
  }, [selectedSubcategories, selectedCategories]);

  return (
    <section ref={reviewRef} id="reviews" className="bg-muted md:py-20 py-8">
      <div className="w-full max-w-7xl mx-auto">
        {/* Section Header */}
        <SectionHeader
          title="Technology Reviews"
          description="Discover the best business technology through authentic user
            reviews, expert analysis, and comprehensive comparisons."
        />
        {/* Filters and Controls */}
        <div className="flex flex-col lg:flex-row gap-6 md:mb-8 mb-4 justify-between">
          <CategoryFilter
            categories={transformCategories(categories) || []}
            subCategoryKey="subcategories"
            parentSection="product"
            selectedCategoryId={selectedCategories || ""}
            selectedSubCategoryId={selectedSubcategories || ""}
            onCategorySelect={(categoryId) => {
              dispatch(updateSelectedCategories(categoryId || ""));
              const categorySelected = categories?.find(
                (category: GenericObjectInterface) => category.documentId === categoryId
              );
              const subcategorySelected = categorySelected?.subcategories?.find(
                (subCat: GenericObjectInterface) => subCat?.contentCount?.["product"] > 0
              );
              if (subcategorySelected?.documentId) {
                dispatch(updateSelectedSubcategories(subcategorySelected.documentId));
              }
            }}
            onSubCategorySelect={(subCategoryId) => {
              dispatch(updateSelectedSubcategories(subCategoryId));
              setPage(1);
            }}
            variant="horizontal"
            className="w-full"
          />
        </div>
        {/* Software Grid/List */}

        <>
          {isLoading ? (
            <div className={`w-full`}>
              <SkeletonLoader />
            </div>
          ) : productDetails && productDetails?.data?.length > 0 ? (
            <div
              className={`px-6 2xl:p-0 grid gap-6 ${"md:grid-cols-3 lg:grid-cols-3"}`}
            >
              {productDetails &&
                productDetails?.data?.map((product: GenericObjectInterface) => {
                  console.log(product, "product in softwarereviewsection");
                  return (
                    <Card
                      key={product?.id}
                      className={`cursor-pointer transition-all p-4 ${comparedProducts?.selectedForComparison?.find(
                        (item) => item.documentId === product?.documentId
                      )
                        ? "ring-2 ring-primary bg-primary/5"
                        : "hover:shadow-md"
                        }`}
                      onClick={() =>
                        router.push(`/products/${product?.slug}`)
                      }
                    >
                      <CardHeader className="">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="text-xs">
                              {product?.product_logo_url ? (
                                <Image
                                  src={`${product?.product_logo_url}`}
                                  alt="Product image placeholder."
                                  width={80}
                                  height={80}
                                  className="object-contain bg-white rounded-sm h-[64px] w-[64px]"
                                  onError={(e) => {
                                    e.currentTarget.src = images.dummyProduct;
                                  }}
                                />
                              ) : (
                                <Image
                                  src={images.dummyProduct}
                                  alt="Product image placeholder."
                                  width={80}
                                  height={80}
                                  className="object-cover bg-muted rounded-sm h-[64px] w-[64px]"
                                  onError={(e) => {
                                    e.currentTarget.src = images.dummyProduct;
                                  }}
                                />
                              )}
                            </div>
                            <div>
                              <CardTitle className="text-sm group-hover:text-primary transition-colors capitalize">
                                {product?.name}
                                {/* {product.verified && (
                          <Badge className="ml-2 bg-success text-success-foreground text-xs">
                            Verified
                          </Badge>
                        )} */}
                              </CardTitle>
                              <div className="flex flex-wrap gap-1">
                                {product?.category_references?.slice(0, 2).map((category: GenericObjectInterface) => (
                                  <Badge key={category?.id} variant="outline" className="text-xs">
                                    {category?.name || "NA"}
                                  </Badge>
                                ))}
                                <span> ...</span>
                              </div>
                            </div>
                          </div>
                          {product?.trending && (
                            <Badge className="text-xs">
                              <TrendingUp className="h-3 w-3 mr-1" />
                              Trending
                            </Badge>
                          )}
                        </div>

                        {/* Rating */}
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center">
                            {renderStars(product?.average_stars)}
                          </div>
                          <span className="font-semibold">
                            {product.rating}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="h-[100px] flex flex-col justify-between">
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {product?.description}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1 p-0 m-0">
                          {product?.category_references?.search_terms?.map(
                            (tag: string) => (
                              <Badge
                                key={tag}
                                variant="secondary"
                                className="text-xs m-0"
                              >
                                {tag}
                              </Badge>
                            )
                          )}
                        </div>

                        {/* Pros/Cons */}
                        {/* <div className="grid grid-cols-2 gap-4 mb-4 text-xs overflow-hidden">
                      <div>
                        <p className="font-medium text-success mb-1">Pros:</p>
                        <ul className="space-y-1 line-clamp-3">
                          {normalizeProsCons(product?.pros).length > 0 ? (
                            normalizeProsCons(product?.pros).map(
                              (pro: string, i: number) => (
                                <li
                                  key={i}
                                  className="text-muted-foreground capitalize"
                                >
                                  • {pro.trim().replace(/^"|"$/g, "")}
                                </li>
                              )
                            )
                          ) : (
                            <li className="text-muted-foreground">Not Found</li>
                          )}
                        </ul>
                      </div>
                      <div>
                        <p className="font-medium text-destructive mb-1">
                          Cons:
                        </p>
                        <ul className="space-y-1 line-clamp-3">
                          {consArray.length > 0 ? (
                            consArray.map((con: string, i: number) => (
                              <li
                                key={i}
                                className="text-muted-foreground capitalize"
                              >
                                • {con.trim().replace(/^"|"$/g, "")}
                              </li>
                            ))
                          ) : (
                            <li className="text-muted-foreground">Not Found</li>
                          )}
                        </ul>
                      </div>
                    </div> */}

                        {/* Price and Actions */}
                        <div className="flex items-center justify-between border-t border-border">
                          {/* <p className="font-semibold text-xs w-32 truncate">
                          {product?.price_range || "Not Found"}
                        </p> */}

                          <div className="flex items-center gap-2 my-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCompare(product);
                              }}
                              className="flex items-center text-xs p-2 border border-gray-300 rounded-sm cursor-pointer hover:bg-gray-100 disabled:opacity-50"
                            >
                              <Plus className="h-3 w-3 mr-1" /> Compare
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleInquiryModalOpen(product?.documentId);
                              }}
                              className="bg-primary text-xs text-white p-2 border border-gray-300 rounded-sm cursor-pointer disabled:opacity-50"
                            >
                              Contact Me
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center py-16 px-4">
              <div className="relative mb-6">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Package className="w-10 h-10 text-gray-400" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No products found
              </h3>
              <p className="text-muted-foreground text-center max-w-md">
                Try adjusting your filters or browse through different
                categories to discover the perfect software for your needs.
              </p>
            </div>
          )}
        </>
        {/* Pagination Controls */}
        <PaginationButtons
          disabled={isLoading}
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(page: number) => {
            if (reviewRef.current) {
              reviewRef.current?.scrollIntoView();
            }
            setPage(page);
          }}
        />
        {comparedProducts?.selectedForComparison?.length > 0 && (
          <div className="bg-background rounded-lg border overflow-x-auto md:mt-10 mt-6 mx-4">
            <ComparisonTable
              productsSelectedForComparison={comparedProducts?.selectedForComparison.map(
                (item) => item.documentId
              )}
              comparedProducts={comparedProducts?.selectedForComparison}
              handleCompare={handleCompare}
            />
          </div>
        )}

        {toastMessage && (
          <CompareToast
            message={toastMessage}
            onClose={() => setToastMessage(null)}
          />
        )}

        {/* Inquiry Modal */}
        {selectedProductId && (
          <CustomDialog
            open={isInquiryModalOpen}
            onOpenChange={setIsInquiryModalOpen}
            title="Product Inquiry"
            description="Fill out the form below and our team will get back to you shortly."
            width="50"
            height="auto"
          >
            <InquiryForm
              productId={selectedProductId}
              onSuccess={handleInquiryModalClose}
            />
          </CustomDialog>
        )}
      </div>
    </section >
  );
};

export default SoftwareReviewsSection;
